import React from 'react';
import { useSearchParams } from 'react-router-dom';

// Centralized components from your shared library
import { SearchPage, SearchPageGrid, SearchPageResultGroup } from 'randmarcomps';

// Portal-specific hooks and components
import { useManufacturerSearch } from '../components/search/useManufacturerSearch';
import { ManufacturerGridItem } from '../components/search/ManufacturerGridItem';
import SearchResult from '../stores/search/SearchResult';
import { Product } from 'randmar-api-client';

// HOCs for context and routing
import withManufacturer from '../contexts/WithManufacturer';
import withRoot from '../withRoot';

interface SearchProps {
  applicationId: string;
}

/**
 * Defines the grouping logic for manufacturer search results.
 * This function categorizes results into distinct sections for the grid.
 */
const groupManufacturerResults = (results: SearchResult[], applicationId: string): Record<string, SearchPageResultGroup<SearchResult>> => {
  const products = results.filter(r => r.contentType === "Product").sort((a, b) => {
    const aRank = (a.newContent as Product).OverallPercentileRank ?? 0
    const bRank = (b.newContent as Product).OverallPercentileRank ?? 0
    return bRank - aRank
  });

  return {
    'Your Products': {
      items: products.filter(r => (r.newContent as Product).ManufacturerId === applicationId),
      gridCols: { base: 1, sm: 2 }
    },
    'Opportunities': {
      items: results.filter(r => r.contentType === 'Opportunity'),
      gridCols: { base: 1, sm: 2 }
    },
    'Resellers': {
      items: results.filter(r => r.contentType === 'Reseller'),
      gridCols: { base: 1, sm: 2 }
    },
    'Other Brands': {
      items: products.filter(r => (r.newContent as Product).ManufacturerId !== applicationId),
      gridCols: { base: 1, md: 2, lg: 3 }
    },
    'Documents': {
      items: results.filter(r => r.contentType === 'Document'),
      gridCols: { base: 1, sm: 2 }
    },
  };
};

const Search: React.FC<SearchProps> = ({ applicationId }) => {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';

  const { results, isLoading } = useManufacturerSearch(query, applicationId);

  const groupResults = React.useCallback(
    (res: SearchResult[]) => groupManufacturerResults(res, applicationId),
    [applicationId]
  );

  return (
    <SearchPage query={query} results={results} isLoading={isLoading}>
      <SearchPageGrid
        results={results}
        groupBy={groupResults}
        renderItem={(item, index) => (
          <ManufacturerGridItem
            key={index}
            searchResult={item}
            applicationId={applicationId}
          />
        )}
      />
    </SearchPage>
  );
};

// The original HOC wrapper remains the same
export default withRoot(withManufacturer(Search));
